import { useState } from 'react';

import { Menu, MenuItem, Typography } from '@mui/material';
import { Box, Stack, useTheme } from '@mui/system';

import VectorImage from '~components/media/vectorImage';
import i18n from '~lib/i18n';
import { Dimens } from '~themes';

interface LanguageSelectorProps {
	iconColor?: 'black' | 'white';
}

const LanguageSelector = ({ iconColor = 'white' }: LanguageSelectorProps) => {
	const { palette } = useTheme();
	const [open, setOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	const allLanguages =
		(i18n.options.supportedLngs as string[] | undefined)
			?.filter((lng) => lng !== 'cimode' && lng !== 'dev')
			.map((language) => ({
				id: `${language}-${language.toUpperCase()}`,
				name: new Intl.DisplayNames(i18n.language, { type: 'language' }).of(language) || language,
			})) || [];

	const openMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		setOpen(true);
		setAnchorEl(event.currentTarget);
	};

	const closeMenu = () => {
		setOpen(false);
		setAnchorEl(null);
	};

	const handleLanguageChange = (language: string) => {
		i18n.changeLanguage(language);
		closeMenu();
	};

	const getFlagImageUrl = (language: string, size: number): string => {
		return `https://flagcdn.com/w${size}/${language.toLowerCase().split('-')[0].replace('en', 'gb')}.png`;
	};

	return (
		<Box>
			<Stack
				direction='row'
				alignItems='center'
				gap={Dimens.spacingXs}
				sx={{ cursor: 'pointer' }}
				onClick={(e) => {
					openMenu(e);
				}}
			>
				<VectorImage name='icon/globe' color={iconColor} />{' '}
				<VectorImage name='chevron/down' color={iconColor} />
			</Stack>
			<Menu
				open={open}
				anchorEl={anchorEl}
				onClose={closeMenu}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				sx={{
					borderRadius: Dimens.radiusMd,
					marginTop: Dimens.spacingMd,
					padding: Dimens.spacingXs,
				}}
			>
				{allLanguages.map((language) => (
					<MenuItem
						key={language.id}
						onClick={() => handleLanguageChange(language.id)}
						sx={{
							borderRadius: Dimens.radiusMd,
							padding: Dimens.spacingMd,
						}}
					>
						<Stack direction='row' alignItems='center' gap={Dimens.spacingMd}>
							<img
								loading='lazy'
								width='24'
								height='18'
								srcSet={`${getFlagImageUrl(language.id, 80)} 2x`}
								src={getFlagImageUrl(language.id, 40)}
								alt=''
								style={{ borderRadius: Dimens.radius2xs }}
							/>
							<Typography variant='labelSm' color={palette.textPrimary.main}>
								{language.name}
							</Typography>
						</Stack>
					</MenuItem>
				))}
			</Menu>
		</Box>
	);
};

export default LanguageSelector;
